'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'

interface Branding {
  name: string
  logoUrl: string | null
  primaryColor: string
  accentColor: string
}

const DEFAULT_BRANDING: Branding = {
  name: 'NetelliVoice Portal',
  logoUrl: null,
  primaryColor: '#2563eb',
  accentColor: '#1d4ed8',
}

export default function LoginForm({ host }: { host: string }) {
  const router = useRouter()
  const [branding, setBranding] = useState<Branding>(DEFAULT_BRANDING)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetch(`/api/branding?host=${encodeURIComponent(host)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setBranding(data) })
      .catch(() => {})
  }, [host])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })

      const data = await res.json()

      if (!res.ok) {
        setError(data.error ?? 'Something went wrong')
        return
      }

      router.push(data.redirectTo)
    } catch {
      setError('Unable to connect. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const { name, logoUrl, primaryColor, accentColor } = branding

  return (
    <>
      <div className="text-center mb-8">
        {logoUrl ? (
          <img src={logoUrl} alt={name} className="h-12 mx-auto mb-4 object-contain" />
        ) : (
          <div
            className="inline-block w-12 h-12 rounded-xl mb-4"
            style={{ backgroundColor: primaryColor }}
          />
        )}
        <h1 className="text-2xl font-bold text-gray-900">{name}</h1>
        <p className="text-sm text-gray-500 mt-1">Sign in to your account</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 px-4 text-white font-medium rounded-md shadow-sm transition-opacity disabled:opacity-60 cursor-pointer"
            style={{ backgroundColor: loading ? primaryColor : primaryColor }}
            onMouseEnter={e => { if (!loading) (e.currentTarget as HTMLElement).style.backgroundColor = accentColor }}
            onMouseLeave={e => { if (!loading) (e.currentTarget as HTMLElement).style.backgroundColor = primaryColor }}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    </>
  )
}
